import { useMemo } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { adminButton, adminTokens } from '../admin-ui';
import type { ModalPresentation } from './ModalShell';

export interface ModalFooterAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  ariaLabel: string;
  ariaBusy?: boolean;
  variant: 'secondary' | 'primary';
}

interface ModalFooterProps {
  onCancel: () => void;
  cancelDisabled?: boolean;
  cancelLabel?: string;
  actions?: ModalFooterAction[];
  /** Match the enclosing ModalShell's presentation. */
  presentation?: ModalPresentation;
}

export function ModalFooter({
  onCancel,
  cancelDisabled = false,
  cancelLabel = 'Cancel',
  actions = [],
  presentation = 'default',
}: ModalFooterProps) {
  const { tokens } = useTheme();
  const styles = useMemo(
    () => (presentation === 'admin' ? adminFooterStyles : createStyles(tokens)),
    [tokens, presentation]
  );

  return (
    <div style={styles.footer}>
      <button
        style={{
          ...styles.cancelButton,
          ...(cancelDisabled ? styles.buttonDisabled : {}),
        }}
        onClick={onCancel}
        type="button"
        disabled={cancelDisabled}
        aria-label={cancelLabel === 'Cancel' ? 'Cancel and close modal' : cancelLabel}
      >
        {cancelLabel}
      </button>
      {actions.map((action) => {
        const baseStyle =
          action.variant === 'primary' ? styles.primaryButton : styles.secondaryButton;
        const disabledStyle = styles.buttonDisabled;

        return (
          <button
            key={`${action.variant}-${action.label}`}
            type="button"
            style={{
              ...baseStyle,
              ...(action.disabled ? disabledStyle : {}),
            }}
            onClick={action.onClick}
            disabled={action.disabled}
            aria-label={action.ariaLabel}
            aria-busy={action.ariaBusy}
          >
            {action.label}
          </button>
        );
      })}
    </div>
  );
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, React.CSSProperties> {
  return {
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '8px',
      padding: '16px 20px',
      borderTop: `1px solid ${tokens.text.subtle}`,
      backgroundColor: tokens.backgrounds.surface,
    },
    cancelButton: {
      padding: '8px 16px',
      backgroundColor: 'transparent',
      color: tokens.text.muted,
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '6px',
      fontSize: '14px',
      cursor: 'pointer',
      minWidth: '44px',
      minHeight: '44px',
    },
    secondaryButton: {
      padding: '8px 16px',
      backgroundColor: tokens.backgrounds.lessonSectionTinted,
      color: tokens.text.primary,
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '6px',
      fontSize: '14px',
      cursor: 'pointer',
      minWidth: '44px',
      minHeight: '44px',
    },
    primaryButton: {
      padding: '8px 16px',
      backgroundColor: tokens.ui.selectionBorder,
      color: tokens.text.onBrand,
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
      minWidth: '44px',
      minHeight: '44px',
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };
}

const adminFooterStyles: Record<string, React.CSSProperties> = {
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: adminTokens.spacing.sm,
    padding: `${adminTokens.spacing.lg} ${adminTokens.spacing.xl}`,
    borderTop: `1px solid ${adminTokens.border.subtle}`,
    backgroundColor: adminTokens.surface.panel,
  },
  cancelButton: { ...adminButton.secondary, minHeight: '44px', minWidth: '44px' },
  secondaryButton: { ...adminButton.secondary, minHeight: '44px', minWidth: '44px' },
  primaryButton: { ...adminButton.primary, minHeight: '44px', minWidth: '44px' },
  buttonDisabled: adminButton.disabled,
};
